import {
  decodeURIComponentSafe,
  makeSafeURIFunction,
} from '@wandb/common/util/url';
import {Buffer} from 'buffer';
import {matchPath} from 'react-router';

import {
  GALLERY_PATH,
  GALLERY_POST_EDIT_PATH,
  GALLERY_POST_VIEW_PATH,
  OLD_GALLERY_DISCUSSION_EDIT_PATH,
  OLD_GALLERY_DISCUSSION_VIEW_PATH,
  REPORT_PAGE_VIEW_PATH,
  REPORT_PAGE_VIEW_PUBLISHED_WORK_PATH,
} from '../../routes/paths';

export const encodeURISafe = makeSafeURIFunction(encodeURI);
export const decodeURISafe = makeSafeURIFunction(decodeURI);
export const encodeURIComponentSafe = makeSafeURIFunction(encodeURIComponent);

export function encodeURIIfNotYetEncoded(uri: string): string {
  const decoded = decodeURISafe(uri);

  const isAlreadyEncoded = uri !== decoded;
  if (isAlreadyEncoded) {
    return uri;
  }

  return encodeURISafe(uri);
}

// Assumes id is a base64 encoded string
export function makeNameAndID(id: string, name?: string): string {
  // Note we strip base64 = padding to make this pretty.
  // It's added back in parseNameAndID.
  id = id.replace(/=/g, '');
  if (name != null) {
    // Replace all non word characters with dashes, eliminate repeating dashes
    name = name.replace(/\W/g, '-').replace(/-+/g, '-');
  }
  return name != null ? `${encodeURIComponentSafe(name)}--${id}` : id;
}

export type ReportNameAndID = {id: string; name: string};

// Note: start using parseURLforReport in /urls instead
// Assumes s is <name>--<id> where <id> is a base64 encoded string
export function parseNameAndID(s: string): ReportNameAndID {
  let id = s;
  let name = '';
  const sepPos = s.lastIndexOf('--');
  if (sepPos !== -1) {
    name = decodeURIComponentSafe(s.slice(0, sepPos)).replace(/-/g, ' ');
    id = s.slice(sepPos + 2, s.length);
  }

  id = addBase64Padding(id);

  if (base64IDToViewID(id) == null) {
    throw new Error('Attempted to parse invalid View ID');
  }
  return {name, id};
}

// We strip base64 `=` padding to make report IDs pretty.
// We do this because a report's ID is included in the URL.
// We have to add it back before using it for other purposes.
// Otherwise, we'll run into issues because we're using invalid IDs.
export function addBase64Padding(s: string): string {
  const pad = 4 - (s.length % 4);
  if (pad !== 4) {
    for (let i = 0; i < pad; i++) {
      s += '=';
    }
  }
  return s;
}

export function parseNameAndIDFromURL(url: string): ReportNameAndID {
  try {
    const nameAndID = url
      .split('?')[0] // remove querystring
      .replace(/\/$/, '') // remove trailing slash
      .split('/') // get last portion of URL
      .slice(-1)[0];
    return parseNameAndID(nameAndID);
  } catch (e) {
    throw new Error('Attempted to parse invalid URL');
  }
}

export function parseNameAndIDFromURLSafe(url: string): ReportNameAndID | null {
  try {
    return parseNameAndIDFromURL(url);
  } catch (e) {
    return null;
  }
}

export function parseNameAndIDSafe(s: string): ReportNameAndID | null {
  try {
    return parseNameAndID(s);
  } catch {
    return null;
  }
}

export function base64IDToViewID(base64: string): number | null {
  const decoded = Buffer.from(base64, 'base64').toString();
  if (decoded.indexOf(':') === -1) {
    return null;
  }
  const [viewString, numericID] = decoded.split(':', 2);
  if (viewString !== 'View' || !numericID.match(/^-{0,1}\d+$/)) {
    return null;
  }
  return Number(numericID);
}

export function isOnReportView(pathname = window.location.pathname): boolean {
  const match = matchPath(pathname, {
    path: REPORT_PAGE_VIEW_PATH,
    exact: true,
  });
  if (match != null) {
    return true;
  }
  if (isOnPublishedReportView(pathname)) {
    return true;
  }
  return false;
}

export function isOnPublishedReportView(
  pathname = window.location.pathname
): boolean {
  const publishedMatch = matchPath(pathname, {
    path: REPORT_PAGE_VIEW_PUBLISHED_WORK_PATH,
    exact: true,
  });
  return publishedMatch != null;
}

export function isOnGalleryPostPage(
  pathname = window.location.pathname
): boolean {
  return isOnGalleryPostView(pathname) || isOnGalleryPostEdit(pathname);
}

export function isOnGalleryPostView(
  pathname = window.location.pathname
): boolean {
  const match = matchPath(pathname, {
    path: GALLERY_POST_VIEW_PATH,
    exact: true,
  });
  return match != null;
}

export function isOnGalleryPostEdit(
  pathname = window.location.pathname
): boolean {
  const match = matchPath(pathname, {
    path: GALLERY_POST_EDIT_PATH,
    exact: true,
  });
  return match != null;
}

export function isOnGalleryDiscussionPage(
  pathname = window.location.pathname
): boolean {
  return (
    isOnGalleryDiscussionView(pathname) || isOnGalleryDiscussionEdit(pathname)
  );
}

export function isOnGalleryDiscussionView(
  pathname = window.location.pathname
): boolean {
  const match = matchPath(pathname, {
    path: OLD_GALLERY_DISCUSSION_VIEW_PATH,
    exact: true,
  });
  return match != null;
}

export function isOnGalleryDiscussionEdit(
  pathname = window.location.pathname
): boolean {
  const match = matchPath(pathname, {
    path: OLD_GALLERY_DISCUSSION_EDIT_PATH,
    exact: true,
  });
  return match != null;
}

export function isOnFCPage(pathname = window.location.pathname): boolean {
  const match = matchPath(pathname, {path: GALLERY_PATH});
  return match != null;
}

export function canonicalize(url: string): string {
  let canonical = url.split('?')[0];
  if (canonical.endsWith('/')) {
    canonical = canonical.substring(0, canonical.length - 1);
  }
  return canonical;
}
